import { CellClassParams, ICellRendererParams } from "ag-grid-community";
import {
  ColumnDiff,
  RowDiff,
  EntityRegistry,
  FieldConfigShort,
} from "@renewmap/model";
import { Checkbox } from "@renewmap/ui/checkbox";
import { MoveRight, Pentagon } from "lucide-react";
import { Geometry, Point } from "geojson";
import { booleanEqual as geometryEqual } from "@turf/boolean-equal";

type DiffStateChange = "nochange" | "modified" | "deleted" | "added" | "lead";

// Get operation type directly from backend RowDiff structure
export const getRowOperationType = (
  rowData: Record<string, ColumnDiff> | unknown,
): DiffStateChange => {
  if (!rowData || typeof rowData !== "object") {
    return "nochange";
  }

  const data = rowData as { operation_type?: DiffStateChange };

  // Access operation_type directly from backend
  return data.operation_type || "nochange";
};

// Styling configuration for operation types
export const getOperationStyles = (operationType: string) => {
  const baseStyles = {
    oldValueClass: "text-red-600 line-through",
    newValueClass: "text-green-500 font-bold",
    oldCheckboxClass: "bg-red-300 line-through",
    newCheckboxClass: "bg-success",
  };

  let operationStyles;
  switch (operationType) {
    case "insert":
      operationStyles = {
        badge: { bgColor: "bg-green-600", text: "Insert" },
        backgroundColor: "oklch(0.962 0.044 156.743)", // Green
        showNew: true,
        showOld: false,
      };
      break;
    case "update":
      operationStyles = {
        badge: { bgColor: "bg-blue-500", text: "Update" },
        backgroundColor: "oklch(0.953 0.058 253.084)", // Blue
        showNew: true,
        showOld: true,
      };
      break;
    case "delete":
      operationStyles = {
        badge: { bgColor: "bg-red-600", text: "Delete" },
        backgroundColor: "oklch(0.936 0.032 17.717)", // Red
        showNew: false,
        showOld: true,
      };
      break;
    case "lead":
      operationStyles = {
        badge: { bgColor: "bg-orange-500", text: "Lead" },
        backgroundColor: "oklch(0.955 0.085 55.732)", // Orange
        showNew: true, // Show draft value
        showOld: true, // Show public value
      };
      break;
    case "nochange":
    default:
      // For unchanged values, return plain styling without diff highlights
      operationStyles = {
        badge: { bgColor: "bg-gray-400", text: "-" },
        backgroundColor: "white",
        showNew: true,
        showOld: false,
        oldValueClass: "",
        newValueClass: "",
        oldCheckboxClass: "",
        newCheckboxClass: "",
      };
      break;
  }

  // Merge base styles with operation-specific styles, with operation styles overriding
  return { ...baseStyles, ...operationStyles };
};

export const getFieldValueString = (value: unknown) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.join(", ");
  }

  return JSON.stringify(value, null, 2);
};
export const defaultCellStyle = ({
  column,
  value,
  data,
}: CellClassParams<unknown, ColumnDiff>) => {
  if (isValueNull(value)) {
    return { backgroundColor: "white" };
  }

  // Check if this specific field has changed
  const fieldChangeState = hasValueChanged(column.getId(), value);

  // Get operation type to check for special cases like "lead"
  const operationType = getRowOperationType(data);

  // Only apply styling if the field has changed OR if it's a "lead" row
  // "lead" rows need styling even when values haven't changed because the entire row is being removed
  if (fieldChangeState === "nochange" && operationType !== "lead") {
    return { backgroundColor: "white" };
  }

  // Apply background color based on operation type
  const styles = getOperationStyles(operationType);
  return { backgroundColor: styles.backgroundColor };
};

export const defaultDiffCellRenderer = ({
  value,
  data,
}: ICellRendererParams<unknown, ColumnDiff>) => {
  if (isValueNull(value)) {
    return (
      <div className="flex p-1">
        <div className="my-auto rounded bg-gray-100 p-1 px-2 text-sm">NULL</div>
      </div>
    );
  }

  // Determine operation type for the entire row to customize rendering
  const operationType = (data as RowDiff)?.operation_type;
  const styles = getOperationStyles(operationType);

  if (typeof value.draft === "boolean" || typeof value.public === "boolean") {
    // For unchanged values, check if row has special operation type (e.g., lead)
    if (value.draft === value.public) {
      // For "lead" rows, show the value with special styling even though it hasn't changed
      if (operationType === "lead") {
        return (
          <div className="flex gap-2 px-1 py-2">
            <Checkbox
              className="text-orange-600"
              checked={Boolean(value.draft)}
              size="lg"
            />
          </div>
        );
      }

      // For other unchanged values, render normally
      return (
        <div className="flex gap-2 px-1 py-2">
          <Checkbox checked={Boolean(value.draft)} size="lg" />
        </div>
      );
    }

    // Use showOld and showNew flags to conditionally render values
    // Consolidates rendering logic for Delete (old only), Insert (new only), and Update (both)
    return (
      <div className="flex gap-2 px-1 py-2">
        {styles.showOld && (
          <Checkbox
            className={styles.oldCheckboxClass}
            checked={Boolean(value.public)}
            size="lg"
          />
        )}
        {styles.showNew && styles.showOld && (
          <MoveRight className="my-auto" size={16} />
        )}
        {styles.showNew && (
          <Checkbox
            className={styles.newCheckboxClass}
            checked={Boolean(value.draft)}
            size="lg"
          />
        )}
      </div>
    );
  } else {
    const publicValue = getFieldValueString(value.public);
    const draftValue = getFieldValueString(value.draft);

    // For unchanged values, check if row has special operation type (e.g., lead)
    if (draftValue === publicValue) {
      // For "lead" rows, show the value with orange text even though it hasn't changed
      if (operationType === "lead") {
        return (
          <span className="text-orange-600 line-through">{draftValue}</span>
        );
      }

      // For other unchanged values, render normally
      return draftValue;
    }

    return (
      <p className="flex h-[39px] w-full gap-3 px-1">
        {styles.showOld && (
          <span className={styles.oldValueClass}>{publicValue || "NULL"}</span>
        )}
        {styles.showNew && styles.showOld && (
          <MoveRight className="my-auto" size={16} />
        )}
        {styles.showNew && (
          <span className={styles.newValueClass}>{draftValue || "NULL"}</span>
        )}
      </p>
    );
  }
};

const toCoordinates = (value: Point) => {
  return value.coordinates.join(", ");
};

export const pointCellRenderer = ({
  value,
}: ICellRendererParams<unknown, ColumnDiff>) => {
  if (!value || (value.public === null && value.draft === null)) {
    return (
      <div className="flex p-1">
        <div className="my-auto rounded bg-gray-100 p-1 px-2 text-sm">NULL</div>
      </div>
    );
  }

  if (JSON.stringify(value.public) === JSON.stringify(value.draft)) {
    return toCoordinates(value.draft as Point);
  }

  const publicValue = value.public
    ? toCoordinates(value.public as Point)
    : null;
  const draftValue = value.draft ? toCoordinates(value.draft as Point) : null;

  return (
    <p className={"flex h-[39px] w-full gap-3 px-1"}>
      {publicValue ? <s className="text-red-600">{publicValue}</s> : null}
      <MoveRight className="my-auto" size={16} />
      <strong>
        {draftValue ? (
          <strong className="text-green-500">{draftValue}</strong>
        ) : null}
      </strong>
    </p>
  );
};

export const geomCellStyle = ({
  value,
  data,
}: CellClassParams<unknown, ColumnDiff>) => {
  if (!value || (!value.public && !value.draft)) {
    return { backgroundColor: "white" };
  }

  // Check if the geometry field has changed
  const fieldChangeState = hasValueChanged("geom_area", value);

  // Get operation type to check for special cases like "lead"
  const operationType = getRowOperationType(data);

  // Only apply styling if the field has changed OR if it's a "lead" row
  // "lead" rows need styling even when values haven't changed because the entire row is being removed
  if (fieldChangeState === "nochange" && operationType !== "lead") {
    return { backgroundColor: "white" };
  }

  // Apply background color based on operation type
  const styles = getOperationStyles(operationType);
  return { backgroundColor: styles.backgroundColor };
};

export const geomAreaCellRenderer = ({
  value,
}: ICellRendererParams<unknown, ColumnDiff>) => {
  if (isValueNull(value)) {
    return (
      <div className="flex p-1">
        <div className="my-auto rounded bg-gray-100 p-1 px-2 text-sm">NULL</div>
      </div>
    );
  }

  return <Pentagon className="m-1 mt-2" size={18} />;
};

export const UnchangedLozenge = ({ label }: { label: string }) => (
  <div className="shrink-0 rounded border border-gray-200 bg-white p-1 px-2 text-sm text-gray-800">
    {label}
  </div>
);

const AddedLozenge = ({ label }: { label: string }) => (
  <div className="shrink-0 rounded border border-green-100 bg-white p-1 px-2 text-sm font-bold text-green-500">
    {label}
  </div>
);

const RemovedLozenge = ({ label }: { label: string }) => (
  <div className="shrink-0 rounded border border-red-100 bg-white p-1 px-2 text-sm text-red-600 line-through">
    {label}
  </div>
);

// Renders an entity id column (e.g. entity_links.source_id) as an entity badge
export const entityIdCellRenderer = ({
  value,
  data,
  entityRegistry,
}: ICellRendererParams<unknown, ColumnDiff<string>> & {
  entityRegistry: EntityRegistry;
}) => {
  if (isValueNull(value)) {
    return (
      <div className="flex p-1">
        <div className="my-auto rounded bg-gray-100 p-1 px-2 text-sm">NULL</div>
      </div>
    );
  }

  // entityRegistry is sourced from draft.v_entity_registry only, so a
  // delete's public-only id will miss the lookup and render as a raw uuid.
  const resolve = (id: string | null) =>
    id ? (entityRegistry[id]?.entity_name ?? id) : "";

  // If the value hasn't changed, just show the resolved name in a badge
  if (value.draft === value.public) {
    const name = value.draft ? resolve(value.draft) : resolve(value.public);
    return (
      <div className="flex items-center p-1">
        <UnchangedLozenge label={name} />
      </div>
    );
  }
  // removed -> added if the value has changed
  const operationType = (data as RowDiff)?.operation_type;
  const styles = getOperationStyles(operationType);

  return (
    <div className="flex items-center gap-2 p-1">
      {styles.showOld && <RemovedLozenge label={resolve(value.public)} />}
      {styles.showNew && styles.showOld && (
        <MoveRight className="my-auto" size={16} />
      )}
      {styles.showNew && <AddedLozenge label={resolve(value.draft)} />}
    </div>
  );
};

// Renders field_values_v1.value_string_array. Fields typed "entity_id_array"
// render as entity badges (added/removed/unchanged).  Default otherwise.
export const valueStringArrayCellRenderer = ({
  value,
  data,
  entityRegistry,
  fieldConfigByKey,
  ...rest
}: ICellRendererParams<unknown, ColumnDiff<string[]>> & {
  entityRegistry: EntityRegistry;
  fieldConfigByKey: Map<string, FieldConfigShort>;
}) => {
  if (isValueNull(value)) {
    return (
      <div className="flex p-1">
        <div className="my-auto rounded bg-gray-100 p-1 px-2 text-sm">NULL</div>
      </div>
    );
  }

  const row = data as RowDiff | undefined;
  const entityType = (row?.values?.["entity_type"] as ColumnDiff<string>)
    ?.draft;
  const fieldName = (row?.values?.["field_name"] as ColumnDiff<string>)?.draft;
  const fieldType =
    entityType && fieldName
      ? fieldConfigByKey.get(`${entityType}::${fieldName}`)?.field_type
      : undefined;

  if (fieldType !== "entity_id_array") {
    return defaultDiffCellRenderer({
      value,
      data,
      ...rest,
    } as ICellRendererParams<unknown, ColumnDiff>);
  }

  const resolve = (id: string) => entityRegistry[id]?.entity_name ?? id;
  const draftSet = new Set(value.draft ?? []);
  const publicSet = new Set(value.public ?? []);

  const added = [...draftSet.difference(publicSet)];
  const removed = [...publicSet.difference(draftSet)];
  const unchanged = [...draftSet.intersection(publicSet)];

  return (
    <div className="flex flex-nowrap items-center gap-1 overflow-x-auto p-1">
      {unchanged.map((id) => (
        <UnchangedLozenge key={id} label={resolve(id)} />
      ))}
      {added.map((id) => (
        <AddedLozenge key={id} label={resolve(id)} />
      ))}
      {removed.map((id) => (
        <RemovedLozenge key={id} label={resolve(id)} />
      ))}
    </div>
  );
};

export const isValueNull = (
  value: ColumnDiff | null | undefined,
): value is null | undefined =>
  !value || (value.public === null && value.draft === null);

export const nullEqualsGeometryCheck = (value: ColumnDiff) => {
  if (value.public === null && value.draft !== null) {
    return true;
  } else if (value.public !== null && value.draft === null) {
    return true;
  }

  if (value.public !== null && value.draft !== null) {
    return !geometryEqual(value.public as Geometry, value.draft as Geometry);
  }

  return false;
};

const geometryColumns = ["geom_area", "point"];

export const hasValueChanged = (fieldName: string, value: ColumnDiff) => {
  if (isValueNull(value)) {
    return "nochange";
  }

  let state: DiffStateChange = "nochange";

  if (geometryColumns.includes(fieldName)) {
    // compare geometry field types for equality
    if (fieldName === "geom_area" && nullEqualsGeometryCheck(value)) {
      state = "modified";
    } else if (
      fieldName === "Point" &&
      nullEqualsGeometryCheck(value) &&
      JSON.stringify(value.public) !== JSON.stringify(value.draft)
    ) {
      state = "modified";
    }
  } else {
    // compare all other field types for equality
    if (Array.isArray(value.public) && Array.isArray(value.draft)) {
      // order-insensitive: a reorder with no membership change is not a change
      const sortedPublic = [...value.public].sort();
      const sortedDraft = [...value.draft].sort();
      state =
        sortedPublic.length === sortedDraft.length &&
        sortedDraft.every((v, i) => v === sortedPublic[i])
          ? "nochange"
          : "modified";
    } else if (value.public !== value.draft) {
      state = "modified";
    }
  }

  // if it has changed then check if it was added or deleted
  if (state === "modified") {
    if (value.public === null) {
      state = "added";
    } else if (value.draft === null) {
      state = "deleted";
    }
  }

  return state;
};

export const operationTypeCellRenderer = ({ data }: ICellRendererParams) => {
  const operationType = getRowOperationType(data);
  const styles = getOperationStyles(operationType);
  return (
    <div className="flex h-full items-center justify-center">
      <span
        className={`rounded px-2 py-1 text-xs font-semibold text-white ${styles.badge.bgColor}`}
      >
        {operationType}
      </span>
    </div>
  );
};
